<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content">
        <pageaddheader :title="title" :title1="title1" :items="items" />

        <div class="card">
          <div class="card-body">
            <div class="table-top">
              <div class="search-set">
                <div class="search-input">
                  <a class="btn btn-searchset">
                    <img src="../../../assets/img/icons/search-white.svg" alt="img" />
                  </a>
                  <div class="dataTables_filter">
                    <label>
                      <input
                        v-model.trim="filters.search"
                        type="search"
                        class="form-control form-control-sm"
                        placeholder="Search quotation, customer, mobile..."
                        @input="fetchQuotations"
                      />
                    </label>
                  </div>
                </div>
              </div>
              <div class="quotation-filters">
                <select v-model="filters.status" class="form-control" @change="fetchQuotations">
                  <option value="">All Status</option>
                  <option v-for="status in statuses" :key="status" :value="status">{{ status }}</option>
                </select>
                <select v-model="filters.purpose" class="form-control" @change="fetchQuotations">
                  <option value="">All Purpose</option>
                  <option v-for="purpose in purposes" :key="purpose" :value="purpose">{{ purpose }}</option>
                </select>
              </div>
            </div>

            <div class="table-responsive">
              <table class="table">
                <thead>
                  <tr>
                    <th>Quotation No</th>
                    <th>Date</th>
                    <th>Valid Until</th>
                    <th>Customer</th>
                    <th>Purpose</th>
                    <th>Status</th>
                    <th class="text-end">Items</th>
                    <th class="text-end">Grand Total</th>
                    <th class="text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="loading">
                    <td colspan="9" class="text-center py-4">Loading quotations...</td>
                  </tr>
                  <tr v-else-if="!quotations.length">
                    <td colspan="9" class="text-center py-4">No quotations found</td>
                  </tr>
                  <tr v-for="quotation in quotations" :key="quotation.quotationid">
                    <td>{{ quotation.quotationno }}</td>
                    <td>{{ formatDate(quotation.quotationdate) }}</td>
                    <td>{{ formatDate(quotation.validuntil) }}</td>
                    <td>
                      <strong>{{ quotation.customername }}</strong>
                      <span v-if="quotation.mobile" class="d-block text-muted">{{ quotation.mobile }}</span>
                    </td>
                    <td>{{ quotation.purpose }}</td>
                    <td>
                      <span class="badges" :class="statusClass(quotation.status)">{{ quotation.status }}</span>
                    </td>
                    <td class="text-end">{{ quotation.itemcount }}</td>
                    <td class="text-end">{{ money(quotation.netamount) }}</td>
                    <td class="text-end">
                      <button class="btn btn-sm btn-light me-1" title="Preview Quotation" @click="previewQuotation(quotation)">
                        <i class="fa fa-print"></i>
                      </button>
                      <router-link
                        v-if="quotation.status === 'DRAFT'"
                        class="btn btn-sm btn-light me-1"
                        :to="{ name: 'editquotation', query: { id: quotation.quotationid } }"
                        title="Edit"
                      >
                        <i class="fa fa-edit"></i>
                      </router-link>
                      <button
                        v-if="quotation.status === 'DRAFT'"
                        class="btn btn-sm btn-outline-primary me-1"
                        @click="changeStatus(quotation, 'SENT')"
                      >
                        Send
                      </button>
                      <button
                        v-if="quotation.status === 'SENT'"
                        class="btn btn-sm btn-outline-success me-1"
                        @click="changeStatus(quotation, 'ACCEPTED')"
                      >
                        Accept
                      </button>
                      <button
                        v-if="quotation.status === 'SENT'"
                        class="btn btn-sm btn-outline-danger me-1"
                        @click="changeStatus(quotation, 'REJECTED')"
                      >
                        Reject
                      </button>
                      <button
                        v-if="quotation.status === 'ACCEPTED'"
                        class="btn btn-sm btn-submit me-1"
                        @click="convertToSale(quotation)"
                      >
                        Convert
                      </button>
                      <button
                        v-if="quotation.status === 'DRAFT'"
                        class="btn btn-sm btn-light"
                        title="Delete"
                        @click="deleteQuotation(quotation)"
                      >
                        <i class="fa fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="quotation-pager">
              <span>Page {{ page }} of {{ totalPages || 1 }}</span>
              <div>
                <button class="btn btn-cancel me-2" :disabled="page <= 1" @click="movePage(-1)">Previous</button>
                <button class="btn btn-submit" :disabled="page >= totalPages" @click="movePage(1)">Next</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";
import { buildBillPrintHtml, printBillHtml } from "../../../assets/utils/billPrint";
import { getCompanySettingsCached } from "@/services/companySettings";

export default {
  name: "quotationlist",
  data() {
    return {
      title: "Quotation List",
      title1: "Manage estimates before converting to sales",
      items: [{ text: "Add Quotation", addname: "/addquotation" }],
      loading: false,
      quotations: [],
      page: 1,
      pageSize: 10,
      totalPages: 0,
      filters: {
        search: "",
        status: "",
        purpose: "",
      },
      quotationDetails: {},
      detailLoading: {},
      companySettings: null,
      statuses: ["DRAFT", "SENT", "ACCEPTED", "REJECTED", "EXPIRED", "CONVERTED", "CANCELLED"],
      purposes: ["GENERAL", "LOAN", "INSURANCE", "TENDER", "OTHER"],
    };
  },
  mounted() {
    this.fetchQuotations();
  },
  methods: {
    async fetchQuotations() {
      this.loading = true;
      try {
        const { data } = await axios.get("/v1/quotations", {
          params: {
            page: this.page,
            page_size: this.pageSize,
            search: this.filters.search || undefined,
            status: this.filters.status || undefined,
            purpose: this.filters.purpose || undefined,
          },
        });
        this.quotations = data.items || [];
        this.totalPages = data.total_pages || 0;
      } catch (error) {
        this.showError(error, "Failed to load quotations");
      } finally {
        this.loading = false;
      }
    },
    async changeStatus(quotation, status) {
      try {
        await axios.patch(`/v1/quotations/${quotation.quotationid}/status`, { status });
        await Swal.fire({ icon: "success", title: "Updated", text: `Quotation marked as ${status}.`, timer: 1400, showConfirmButton: false });
        await this.fetchQuotations();
      } catch (error) {
        this.showError(error, "Failed to update quotation status");
      }
    },
    async convertToSale(quotation) {
      const result = await Swal.fire({
        icon: "question",
        title: "Convert to Sale?",
        text: "This will create a real sale and apply stock/ledger effects.",
        showCancelButton: true,
        confirmButtonColor: "#FF9F43",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, convert",
      });
      if (!result.isConfirmed) return;
      try {
        const { data } = await axios.post(`/v1/quotations/${quotation.quotationid}/convert`);
        await Swal.fire({ icon: "success", title: "Sale Created", text: `Sale ID: ${data.saleid}` });
        await this.fetchQuotations();
      } catch (error) {
        this.showError(error, "Failed to convert quotation");
      }
    },
    async deleteQuotation(quotation) {
      const result = await Swal.fire({
        icon: "warning",
        title: "Delete draft quotation?",
        text: "This action cannot be undone.",
        showCancelButton: true,
        confirmButtonColor: "#FF9F43",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete",
      });
      if (!result.isConfirmed) return;
      try {
        await axios.delete(`/v1/quotations/${quotation.quotationid}`);
        await Swal.fire({ icon: "success", title: "Deleted", timer: 1400, showConfirmButton: false });
        await this.fetchQuotations();
      } catch (error) {
        this.showError(error, "Failed to delete quotation");
      }
    },
    async fetchCompanySettings() {
      if (this.companySettings) return;
      try {
        this.companySettings = await getCompanySettingsCached();
      } catch (error) {
        console.error(error);
        this.companySettings = {};
      }
    },
    async fetchQuotationDetail(quotationid) {
      if (this.quotationDetails[quotationid] || this.detailLoading[quotationid]) return;
      this.detailLoading = { ...this.detailLoading, [quotationid]: true };
      try {
        const { data } = await axios.get(`/v1/quotations/${quotationid}`);
        this.quotationDetails = { ...this.quotationDetails, [quotationid]: data };
      } finally {
        this.detailLoading = { ...this.detailLoading, [quotationid]: false };
      }
    },
    rowTotal(item) {
      const amount = Number(item.amount || 0);
      const tax = Number(item.taxamount || 0);
      return item.istaxinclusive ? amount : amount + tax;
    },
    escapeHtml(value) {
      return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    },
    async buildQuotationDocument(quotation) {
      const quotationid = quotation?.quotationid;
      if (!quotationid) return null;

      await this.fetchQuotationDetail(quotationid);
      await this.fetchCompanySettings();

      const detail = this.quotationDetails[quotationid];
      if (!detail) {
        Swal.fire({ icon: "error", title: "Quotation Error", text: "Failed to load quotation details." });
        return null;
      }

      const items = detail.items || [];
      const safeDate = (value) => {
        const out = this.formatDate(value);
        return out && out !== "-" ? out : "-";
      };
      const sum = (field) => items.reduce((total, item) => total + Number(item?.[field] || 0), 0);

      return buildBillPrintHtml({
        documentTitle: `Quotation ${detail.quotationno || ""}`,
        title: "Quotation",
        topStripTitle: "QUOTATION",
        company: this.companySettings,
        partyLabel: "Quoted To",
        partyRows: [
          { label: "Customer", value: detail.customername || "-" },
          { label: "Mobile", value: detail.mobile },
          { label: "GSTIN", value: detail.customergstin },
          { label: "Address", value: detail.customeraddress },
        ],
        billRows: [
          { label: "Quotation No", value: detail.quotationno || "" },
          { label: "Date", value: safeDate(detail.quotationdate) },
          { label: "Valid Until", value: safeDate(detail.validuntil) },
          { label: "Purpose", value: detail.purpose },
        ],
        columns: [
          { label: "#", style: "width:26px;" },
          { label: "Particular", className: "item-col" },
          { label: "Qty", className: "r", style: "width:44px;" },
          { label: "Rate", className: "r", style: "width:70px;" },
          { label: "Disc", className: "r", style: "width:50px;" },
          { label: "Taxable", className: "r", style: "width:70px;" },
          { label: "GST%", className: "c", style: "width:42px;" },
          { label: "Tax", className: "r", style: "width:66px;" },
          { label: "Total", className: "r", style: "width:70px;" },
        ],
        rows: items.map((item, index) => {
          const hsn = item.hsncode ? `HSN/SAC: ${this.escapeHtml(item.hsncode)}` : "";
          const incl = item.istaxinclusive ? "Tax inclusive" : "";
          const sub = [hsn, incl].filter(Boolean).join("  |  ");
          return [
            { text: index + 1, className: "c" },
            {
              html: `${this.escapeHtml(item.itemname || item.itemid || "")}${sub ? `<span class="sub">${sub}</span>` : ""}`,
              className: "item-name-cell",
            },
            { text: this.money(item.quantity), className: "r" },
            { text: `Rs. ${this.money(item.rate)}`, className: "r" },
            { text: Number(item.discount || 0) ? this.money(item.discount) : "-", className: "r" },
            { text: `Rs. ${this.money(item.taxablevalue)}`, className: "r" },
            { text: String(Number(item.taxpercent || 0)), className: "c" },
            { text: `Rs. ${this.money(item.taxamount)}`, className: "r" },
            { text: `Rs. ${this.money(this.rowTotal(item))}`, className: "r" },
          ];
        }),
        subTotalRow: [
          { text: "", className: "c" },
          { text: "Sub-Total" },
          { text: this.money(sum("quantity")), className: "r" },
          { text: "" },
          { text: "" },
          { text: `Rs. ${this.money(sum("taxablevalue"))}`, className: "r" },
          { text: "" },
          { text: `Rs. ${this.money(sum("taxamount"))}`, className: "r" },
          { text: `Rs. ${this.money(items.reduce((total, item) => total + this.rowTotal(item), 0))}`, className: "r" },
        ],
        totals: [
          { label: "Taxable Value", value: `Rs. ${this.money(sum("taxablevalue"))}` },
          { label: "CGST", value: `Rs. ${this.money(sum("cgst"))}` },
          { label: "SGST", value: `Rs. ${this.money(sum("sgst"))}` },
          { label: "IGST", value: `Rs. ${this.money(sum("igst"))}` },
          { label: "Discount", value: `- Rs. ${this.money(detail.discountamount || 0)}` },
          { label: "Tax", value: `Rs. ${this.money(detail.taxamount || 0)}` },
          { label: "Round Off", value: `Rs. ${this.money(detail.roundoff || 0)}` },
          { label: "Grand Total", value: `Rs. ${this.money(detail.netamount || 0)}` },
        ],
        footerNote: this.companySettings?.InvoiceFooter || this.companySettings?.InvoiceTerms,
        itemColumnWidth: "30%",
        netAmount: detail.netamount,
      });
    },
    async previewQuotation(quotation) {
      try {
        const html = await this.buildQuotationDocument(quotation);
        if (!html) return;
        printBillHtml(html, (text) => {
          Swal.fire({ icon: "error", title: "Print Error", text });
        });
      } catch (error) {
        this.showError(error, "Failed to preview quotation");
      }
    },
    showError(error, fallback) {
      Swal.fire({
        icon: "error",
        title: "Quotation Error",
        text: error?.response?.data?.detail || fallback,
      });
    },
    movePage(step) {
      this.page += step;
      this.fetchQuotations();
    },
    formatDate(value) {
      if (!value) return "-";
      return new Date(value).toLocaleDateString("en-IN");
    },
    money(value) {
      return Number(value || 0).toFixed(2);
    },
    statusClass(status) {
      if (status === "ACCEPTED" || status === "CONVERTED") return "bg-lightgreen";
      if (status === "REJECTED" || status === "CANCELLED" || status === "EXPIRED") return "bg-lightred";
      if (status === "SENT") return "bg-lightyellow";
      return "bg-lightgrey";
    },
  },
};
</script>

<style scoped>
.quotation-filters {
  display: flex;
  gap: 10px;
}
.quotation-filters .form-control {
  min-width: 150px;
}
.quotation-pager {
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding-top: 16px;
}
.badges {
  min-width: 82px;
  text-align: center;
}
</style>
